import { STORAGE_KEY } from '../const';
import CacheHelper from './CacheHelper';

let cacheCart = {};

const getCartKey = (product) => {
  if (product.selectedOption) {
    return `${product.id}_${product.selectedOption.label.replace(/\s+/g, '_')}`;
  }
  return `${product.id}`;
};

const cartItemCount = (cart) => {
  if (!cart) return 0;
  return Object.values(cart.products)
    .map((p) => p.quantity)
    .reduce((a, b) => a + b, 0);
};

const getTaxType = (shopId) => {
  const shop = CacheHelper.get(`shop-profile-${shopId}`) || CacheHelper.get(`shop-details-${shopId}`) || {};
  return (
    shop?.tax_preference ||
    shop?.tax_type ||
    shop?.config?.tax_preference ||
    shop?.config?.tax_type ||
    'Including Tax'
  ).toString().toLowerCase();
};

const getCart = (shopId) => {
  if (cacheCart[shopId]) {
    _updateCartSummary(shopId, cacheCart[shopId]);
    return cacheCart[shopId];
  }
  const cartStr = localStorage.getItem(`${shopId}-${STORAGE_KEY.CART}`);
  const cart = JSON.parse(cartStr) || {};
  if (!cart.products) {
    cart.products = {};
  }
  _updateCartSummary(shopId, cart);
  cacheCart[shopId] = cart;
  return cart;
};

function reduceFromCart(shopId, product, quantity = 1) {
  const cart = getCart(shopId);
  const key = getCartKey(product);
  const existingCartProduct = cart.products[key];
  if (!existingCartProduct) return cart;
  const updatedQuantity = existingCartProduct.quantity - quantity;
  if (updatedQuantity <= 0) {
    delete cart.products[key];
  } else {
    cart.products[key] = {
      ...existingCartProduct,
      quantity: updatedQuantity,
    };
  }
  _updateCartSummary(shopId, cart);
  saveCart(shopId, cart);
  return cart;
}

function removeFromCart(shopId, product) {
  const cart = getCart(shopId);
  const key = getCartKey(product);
  if (cart.products[key]) {
    delete cart.products[key];
    _updateCartSummary(shopId, cart);
    saveCart(shopId, cart);
  }
}

const addToCart = (shopId, product, quantity) => {
  const cart = getCart(shopId);
  const key = getCartKey(product);
  const existingCartProduct = cart.products[key];
  if (existingCartProduct) {
    cart.products[key] = {
      product,
      quantity: existingCartProduct.quantity + quantity,
    };
  } else {
    cart.products[key] = { product, quantity };
  }
  _updateCartSummary(shopId, cart);
  saveCart(shopId, cart);
  return cart;
};

function setQtyInCart(shopId, product, quantity) {
  const cart = getCart(shopId);
  const key = getCartKey(product);

  if (quantity <= 0) {
    delete cart.products[key];
  } else {
    cart.products[key] = {
      product,
      quantity: quantity,
    };
  }

  _updateCartSummary(shopId, cart);
  saveCart(shopId, cart);
  return cart;
}

function _updateCartSummary(shopId, cart) {
  let total = 0;
  let totalSavings = 0;
  let totalTax = 0;
  let totalCess = 0;

  const taxType = getTaxType(shopId);

  const isExclusive = taxType === 'excluding tax';
  const isInclusive = taxType === 'including tax';
  const isNoTax = taxType === 'no tax' || taxType === 'none';

  Object.keys(cart.products).forEach((productId) => {
    const cartProduct = cart.products[productId];
    const product = cartProduct.product;
    const quantity = cartProduct.quantity;
    const price = product.price || 0;
    const taxRate = isNoTax ? 0 : (product.tax || 0);
    const cessRate = isNoTax ? 0 : (product.cess || 0);

    const lineTotal = price * quantity;

    if (isInclusive) {
      const basePrice = lineTotal / (1 + (taxRate / 100) + (cessRate / 100));
      totalTax += basePrice * (taxRate / 100);
      totalCess += basePrice * (cessRate / 100);
      total += lineTotal;
    } else if (isExclusive) {
      totalTax += lineTotal * (taxRate / 100);
      totalCess += lineTotal * (cessRate / 100);
      total += lineTotal;
    } else {
      // No Tax or other (default to no tax handling if not explicitly incl/excl)
      total += lineTotal;
    }

    if (product.mrp && product.mrp > price) {
      totalSavings += (product.mrp - price) * quantity;
    }
  });

  const tax = totalTax + totalCess;

  const payable = isExclusive ? (total + tax) : total;
  const subTotal = isInclusive ? (total - tax) : total;

  cart.summary = {
    total: Number(payable.toFixed(2)),
    subTotal: Number(subTotal.toFixed(2)),
    tax: Number(tax.toFixed(2)),
    discount: Number(totalSavings.toFixed(2)),
    payable: Number(payable.toFixed(2)),
    totalSavings: Number(totalSavings.toFixed(2)),
    itemCount: cartItemCount(cart)
  };
}

function saveCart(shopId, cart) {
  cacheCart[shopId] = cart;
  const cartStr = JSON.stringify(cart);
  localStorage.setItem(`${shopId}-${STORAGE_KEY.CART}`, cartStr);
}

function clearCart(shopId) {
  cacheCart[shopId] = { products: {} };
  localStorage.removeItem(`${shopId}-${STORAGE_KEY.CART}`);
}

function reOrder(shopId, orderId) {
  clearCart(shopId);
  const orders = JSON.parse(localStorage.getItem(`${shopId}-${STORAGE_KEY.ORDERS}`));
  const order = orders.find((ord) => ord?.id === Number(orderId)) || {};

  const transformedData = order.details.items.reduce((acc, item) => {
    if (item.id) {
      acc[item.id] = {
        product: {
          id: item.id,
          images: [item.image],
          price: item.rate,
          name: item.name,
          tax: item.tax || 0,
          cess: item.cess || 0,
        },
        quantity: item.quantity,
      };
    }
    return acc;
  }, {});

  const cart = {
    products: transformedData,
  };

  _updateCartSummary(shopId, cart);
  saveCart(shopId, cart);
}

const CartHelper = {
  cartItemCount,
  getCart,
  addToCart,
  clearCart,
  removeFromCart,
  reduceFromCart,
  setQtyInCart,
  reOrder,
  getTaxType,
};

export default CartHelper;
